import { FC, Fragment } from "react";
import {
  capitalize,
  IconButton,
  Popover,
  PopoverOrigin,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { formatDate } from "@/utils/helpers/date-time/datetime-format";
import { ResourceActionExecution } from "@/services/cloudchipr.api";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { getResourceTypeName } from "@/utils/helpers/resources/getResourceTypeName";
import { CopyIconButton } from "@/components/common/CopyIconButton";
import { lastExecutionStateIcon } from "@/components/pages/automations/workflows/data-grid/components/columns/last-execution-cell/utils/constants/constants";

interface WorkflowLastExecutionPopoverProps {
  open: boolean;
  onClose(): void;
  anchor: HTMLElement | null;
  lastExecutionResults: ResourceActionExecution[];
  lastExecutionDate: string;
  executionLogsUrl: string;
}

export const WorkflowLastExecutionPopover: FC<
  WorkflowLastExecutionPopoverProps
> = ({
  open,
  onClose,
  anchor,
  lastExecutionResults,
  lastExecutionDate,
  executionLogsUrl,
}) => {
  return (
    <Popover
      open={open}
      anchorEl={anchor}
      onClose={onClose}
      transformOrigin={transformOrigin}
      anchorOrigin={anchorOrigin}
      slotProps={{
        paper: paperProps,
      }}
    >
      <Stack p={1} spacing={1}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          position="relative"
        >
          <TypographyWithTooltip
            variant="body2"
            title={formatDate(lastExecutionDate, { type: "dateTime" })}
            position="sticky"
            top={0}
          />

          <Tooltip
            title="View In Execution Logs"
            aria-label="Open Execution Logs"
            placement="right"
          >
            <IconButton
              component={RouterLink}
              onClick={onClose}
              to={executionLogsUrl}
              target="_blank"
              size="small"
              sx={{ p: 0, "&:hover": { bgcolor: "transparent" } }}
            >
              <OpenInNewIcon fontSize="small" color="primary" />
            </IconButton>
          </Tooltip>
        </Stack>

        {lastExecutionResults.map((result) => {
          const { type, action, resources } = result;
          const resourceTypeName = getResourceTypeName(type, {
            type: "long",
          });
          const actionLabel = action ? capitalize(action) : "";

          return (
            <Fragment key={type}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1}
              >
                <Typography
                  variant="body2"
                  fontWeight="bold"
                >{`${resourceTypeName} (${resources.length})`}</Typography>
                <Typography variant="body2">{actionLabel}</Typography>
              </Stack>

              {resources.map((resource, index) => {
                const name = resource.name;
                const state = resource.state;
                const stateIcon = state
                  ? lastExecutionStateIcon.get(state)
                  : null;

                return (
                  <Stack
                    key={`${name}-${index}`}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    bgcolor="grey.100"
                    borderRadius={1}
                    px={0.5}
                  >
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <TypographyWithTooltip
                        variant="body2"
                        title={name}
                        pl={0.5}
                      />
                      <CopyIconButton data={name} />
                    </Stack>
                    {stateIcon && (
                      <Tooltip
                        title={state ? capitalize(state) : ""}
                        placement="top"
                      >
                        {stateIcon}
                      </Tooltip>
                    )}
                  </Stack>
                );
              })}
            </Fragment>
          );
        })}
      </Stack>
    </Popover>
  );
};

const anchorOrigin: PopoverOrigin = { vertical: "bottom", horizontal: 0 };
const transformOrigin: PopoverOrigin = {
  vertical: "top",
  horizontal: 0,
};
const paperProps = {
  sx: { minWidth: 320, maxHeight: 400, overflowY: "auto" },
};
